/**
 * The practice's patient list saved on the device: one IndexedDB record per user and practice, encrypted with AES-GCM
 * under the key from GET /patients/group/:groupId/cache-key. The key is held in memory only, so a copied browser
 * profile, another user on the same computer, or someone no longer in the practice cannot read it. A record from
 * another version (list fields or server secret changed) or older than seven days is dropped. Ported from REMOAT's
 * apps/dashboard/src/app/core/saved-copy.ts. The practice's saved settings use it too, under their own key
 * (`cache-key?purpose=practice-settings`).
 */

const DB = 'medicai-offline-store';
const STORE = 'records';
const MAX_AGE_MS = 7 * 24 * 3600_000;

export interface SavedPatientList {
  /** Server time the rows are in sync with. */
  syncedAt: string;
  /** When the whole list was last loaded from the server rather than patched with changes. */
  fullAt: string;
  rows: any[];
}

interface StoredRecord {
  id: string;
  version: string;
  savedAt: number;
  iv: Uint8Array;
  ct: Uint8Array;
}

export interface SavedCopy<T = SavedPatientList> {
  get(): Promise<T | undefined>;
  put(value: T): Promise<void>;
  clear(): Promise<void>;
}

function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: 'id' });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function hex(bytes: ArrayBuffer): Promise<string> {
  return Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('');
}

/** Fails where IndexedDB or WebCrypto is unavailable; the caller then loads from the server as before. */
export async function openSavedCopy<T = SavedPatientList>(keyBase64: string, version: string, groupId: string): Promise<SavedCopy<T>> {
  const raw = Uint8Array.from(atob(keyBase64), c => c.charCodeAt(0));
  const key = await crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']);
  // The record is named by the key, which is per user and practice, so users on one computer never share a record.
  const id = await hex(await crypto.subtle.digest('SHA-256', raw));
  const binding = new TextEncoder().encode(`${id}|${groupId}`);
  const db = await database();
  const store = (mode: IDBTransactionMode) => db.transaction(STORE, mode).objectStore(STORE);
  const drop = () => done(store('readwrite').delete(id)).then(() => undefined);

  return {
    async get() {
      const record = (await done(store('readonly').get(id))) as StoredRecord | undefined;
      if (!record) return undefined;
      if (record.version !== version || Date.now() - record.savedAt > MAX_AGE_MS) return drop();
      try {
        const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: record.iv, additionalData: binding }, key, record.ct);
        return JSON.parse(new TextDecoder().decode(plain));
      } catch {
        return drop();
      }
    },
    async put(value) {
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const ct = new Uint8Array(await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv, additionalData: binding }, key, new TextEncoder().encode(JSON.stringify(value))));
      const record: StoredRecord = { id, version, savedAt: Date.now(), iv, ct };
      await done(store('readwrite').put(record));
    },
    clear: drop,
  };
}
